import { useCallback, useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Text } from '@/components/ui/text';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type { SportActivityRecord } from '~/powersync/generated/schema';
import { SportActivityListItem } from '~/sport-activity/components/sport-activity-list-item/SportActivityListItem';
import { RightSidebar } from '~/layout/components/right-sidebar/RightSidebar';
import { useRightSidebarDrawer } from '~/layout/components/right-sidebar/RightSidebarDrawerContext';

export interface WorkoutFavouritesPanelProps {
  /** The open workout to append exercises to; `null` in create mode. */
  workoutId: string | null;
}

type MostLoggedRow = { sportActivityId: string; exerciseCount: number };

/**
 * Right-rail "favourites" for the workout builder: the sport activities the user
 * uses most across their workouts. Tapping one appends a new superset with that
 * exercise to the open workout, reusing `createSupersetWithExercise`.
 */
export const WorkoutFavouritesPanel = ({
  workoutId,
}: WorkoutFavouritesPanelProps) => {
  const { t } = useTranslation('workout');
  const powerSync = usePowerSync();
  // Present only when hosted by the mobile drawer (`undefined` on desktop).
  const drawer = useRightSidebarDrawer();

  const { data: rows = [] } = useLivePowerSyncQuery<MostLoggedRow>(
    db.Workout.queries.findMostLoggedSportActivities(5),
    [],
  );

  const activityIds = useMemo(
    () => rows.map((r) => r.sportActivityId).filter(Boolean),
    [rows],
  );

  const { data: activities = [] } = useLivePowerSyncQuery<SportActivityRecord>(
    db.SportActivity.queries.findByIds(activityIds),
    [activityIds.join(',')],
  );

  // Preserve the most-used ordering from the aggregate query.
  const ordered = useMemo<SportActivityRecord[]>(() => {
    const byId = new Map(activities.map((a) => [a.id, a]));
    return activityIds
      .map((id) => byId.get(id))
      .filter(Boolean) as SportActivityRecord[];
  }, [activities, activityIds]);

  const addExerciseMutation = useMutation({
    mutationFn: (activity: SportActivityRecord) => {
      if (!workoutId) throw new Error('No workout in context');
      return db.Workout.mutations.createSupersetWithExercise(
        powerSync,
        workoutId,
        {
          id: activity.id,
          defaultDimensions: activity.defaultDimensions ?? '',
        },
      );
    },
  });

  const handleAdd = useCallback(
    (activity: SportActivityRecord) => {
      if (!workoutId) return;
      addExerciseMutation.mutate(activity);
      // On mobile, dismiss the drawer so the user sees the updated workout.
      drawer?.close();
    },
    [workoutId, addExerciseMutation, drawer],
  );

  return (
    <View className="flex-1">
      <RightSidebar.PanelHeader
        icon="Heart"
        title={t('rightSidebar.tabs.favourites')}
      />
      {ordered.length === 0 ? (
        <View className="flex-1 items-center justify-center p-6">
          <Text className="font-inter-regular text-sm text-text-secondary text-center">
            {t('rightSidebar.favourites.empty')}
          </Text>
        </View>
      ) : (
        <ScrollView className="flex-1" contentContainerClassName="py-2">
          {ordered.map((activity, index) => (
            <SportActivityListItem
              key={activity.id}
              activity={activity}
              onPress={() => handleAdd(activity)}
              showBottomBorder={index < ordered.length - 1}
            />
          ))}
        </ScrollView>
      )}
    </View>
  );
};
